import React, { useState, useEffect } from 'react';
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  Button,
  Table,
  Badge,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Form,
  FormGroup,
  Label,
  Input,
  Alert,
  Spinner
} from 'reactstrap';
import { FaPlus, FaEdit, FaTrash, FaUpload } from 'react-icons/fa';
import {
  getTarifasList,
  postTarifasCreate,
  patchTarifasUpdate,
  deleteTarifas,
  postTarifasBulkCreate
} from '../../../helpers/backend';

const Tarifas = () => {
  const [tarifas, setTarifas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(false);
  const [bulkModal, setBulkModal] = useState(false);
  const [editingTarifa, setEditingTarifa] = useState(null);
  const [alert, setAlert] = useState({ show: false, message: '', type: '' });
  const [formData, setFormData] = useState({
    descripcion: '',
    valor_tarifa: '',
    tarifa_especial: '',
    tarifa_lodo: '',
    activa: true
  });
  const [bulkData, setBulkData] = useState('');

  // Opciones predefinidas para descripción
  const descripcionOptions = [
    'Pequ. Prod. Comercial',
    'Gran Prod. Comercial <6',
    'Residencial Estrato 3',
    'Residencial Estrato 2',
    'Gran Prod. Oficial <6',
    'Pequ. Prod. Oficial',
    'Gran Prod. Comercial >6',
    'Multiusuario Residencial Estrato 3',
    'Multiusuario No Residencial Comercial',
    'Residencial Estrato 4',
    'Gran Prod. Oficial >6',
    'Residencial Estrato 1',
    'Multiusuario Residencial Estrato 2',
    'Multiusuario Residencial Estrato 4',
    'Multiusuario Residencial Estrato 5',
    'Residencial Estrato 5',
    'Pequ. Prod. Industrial',
    'Multiusuario No Residencial Oficial',
    'Residencial Estrato 6'
  ];

  useEffect(() => {
    fetchTarifas();
  }, []);

  const fetchTarifas = async () => {
    try {
      setLoading(true);
      const response = await getTarifasList();
      setTarifas(response.data.results || []);
    } catch (error) {
      showAlert('Error al cargar las tarifas', 'danger');
    } finally {
      setLoading(false);
    }
  };

  const showAlert = (message, type) => {
    setAlert({ show: true, message, type });
    setTimeout(() => setAlert({ show: false, message: '', type: '' }), 5000);
  };

  const toggleModal = () => {
    setModal(!modal);
    if (!modal) {
      setEditingTarifa(null);
      setFormData({
        descripcion: '',
        valor_tarifa: '',
        tarifa_especial: '',
        tarifa_lodo: '',
        activa: true
      });
    }
  };

  const toggleBulkModal = () => {
    setBulkModal(!bulkModal);
    setBulkData('');
  };

  const handleEdit = (tarifa) => {
    setEditingTarifa(tarifa);
    setFormData({
      descripcion: tarifa.descripcion,
      valor_tarifa: tarifa.valor_tarifa,
      tarifa_especial: tarifa.tarifa_especial,
      tarifa_lodo: tarifa.tarifa_lodo || '',
      activa: tarifa.activa
    });
    setModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingTarifa) {
        await patchTarifasUpdate(editingTarifa.id, formData);
        showAlert('Tarifa actualizada exitosamente', 'success');
      } else {
        await postTarifasCreate(formData);
        showAlert('Tarifa creada exitosamente', 'success');
      }
      toggleModal();
      fetchTarifas();
    } catch (error) {
      showAlert('Error al guardar la tarifa', 'danger');
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('¿Está seguro de eliminar esta tarifa?')) {
      try {
        await deleteTarifas(id);
        showAlert('Tarifa eliminada exitosamente', 'success');
        fetchTarifas();
      } catch (error) {
        showAlert('Error al eliminar la tarifa', 'danger');
      }
    }
  };

  const handleBulkSubmit = async (e) => {
    e.preventDefault();
    try {
      const data = JSON.parse(bulkData);
      await postTarifasBulkCreate(data);
      showAlert('Tarifas cargadas exitosamente', 'success');
      toggleBulkModal();
      fetchTarifas();
    } catch (error) {
      showAlert('Error al cargar las tarifas. Verifique el formato JSON', 'danger');
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('es-CO', {
      style: 'currency',
      currency: 'COP'
    }).format(value);
  };

  return (
    <div className='page-content'>
    <Container fluid>
      <Row>
        <Col>
          <Card>
            <CardBody>
              <div className="d-flex justify-content-between align-items-center mb-4">
                <h4 className="card-title mb-0">Gestión de Tarifas</h4>
                <div>
                  <Button color="primary" onClick={toggleModal}>
                    <FaPlus className="me-1" />
                    Nueva Tarifa
                  </Button>
                </div>
              </div>

              {alert.show && (
                <Alert color={alert.type} className="mb-3">
                  {alert.message}
                </Alert>
              )}

              {loading ? (
                <div className="text-center py-4">
                  <Spinner color="primary" />
                  <p className="mt-2">Cargando tarifas...</p>
                </div>
              ) : (
                <Table responsive striped>
                  <thead>
                    <tr>
                      <th>Descripción</th>
                      <th>Valor Tarifa</th>
                      <th>Tarifa Especial</th>
                      <th>Lodo Acueducto</th>
                      <th>Estado</th>
                      <th>Acciones</th>
                    </tr>
                  </thead>
                  <tbody>
                    {tarifas.length > 0 ? (
                      tarifas.map((tarifa) => (
                        <tr key={tarifa.id}>
                          <td>{tarifa.descripcion}</td>
                          <td>{formatCurrency(tarifa.valor_tarifa)}</td>
                          <td>{formatCurrency(tarifa.tarifa_especial)}</td>
                          <td>{tarifa.tarifa_lodo ? formatCurrency(tarifa.tarifa_lodo) : '-'}</td>
                          <td>
                            <Badge color={tarifa.activa ? 'success' : 'secondary'}>
                              {tarifa.activa ? 'Activa' : 'Inactiva'}
                            </Badge>
                          </td>
                          <td>
                            <Button
                              color="warning"
                              size="sm"
                              className="me-2"
                              onClick={() => handleEdit(tarifa)}
                            >
                              <FaEdit />
                            </Button>
                            <Button
                              color="danger"
                              size="sm"
                              onClick={() => handleDelete(tarifa.id)}
                            >
                              <FaTrash />
                            </Button>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="6" className="text-center py-4">
                          No hay tarifas registradas
                        </td>
                      </tr>
                    )}
                  </tbody>
                </Table>
              )}
            </CardBody>
          </Card>
        </Col>
      </Row>

      {/* Modal para crear/editar tarifa */}
      <Modal isOpen={modal} toggle={toggleModal}>
        <ModalHeader toggle={toggleModal}>
          {editingTarifa ? 'Editar Tarifa' : 'Nueva Tarifa'}
        </ModalHeader>
        <Form onSubmit={handleSubmit}>
          <ModalBody>
            <FormGroup>
              <Label for="descripcion">Descripción *</Label>
              <Input
                type="select"
                id="descripcion"
                value={formData.descripcion}
                onChange={(e) => setFormData({ ...formData, descripcion: e.target.value })}
                required
              >
                <option value="">Seleccione una descripción</option>
                {descripcionOptions.map((option, index) => (
                  <option key={index} value={option}>
                    {option}
                  </option>
                ))}
              </Input>
            </FormGroup>
            <FormGroup>
              <Label for="valor_tarifa">Valor Tarifa *</Label>
              <Input
                type="number"
                id="valor_tarifa"
                value={formData.valor_tarifa}
                onChange={(e) => setFormData({ ...formData, valor_tarifa: e.target.value })}
                required
                step="0.01"
                min="0"
                placeholder="25000.00"
              />
            </FormGroup>
            <FormGroup>
              <Label for="tarifa_especial">Tarifa Especial *</Label>
              <Input
                type="number"
                id="tarifa_especial"
                value={formData.tarifa_especial}
                onChange={(e) => setFormData({ ...formData, tarifa_especial: e.target.value })}
                required
                step="0.01"
                min="0"
                placeholder="20000.00"
              />
            </FormGroup>
            <FormGroup>
              <Label for="tarifa_lodo">Lodo Acueducto</Label>
              <Input
                type="number"
                id="tarifa_lodo"
                value={formData.tarifa_lodo}
                onChange={(e) => setFormData({ ...formData, tarifa_lodo: e.target.value })}
                step="0.01"
                min="0"
                placeholder="0.00"
              />
            </FormGroup>
            <FormGroup check>
              <Input
                type="checkbox"
                id="activa"
                checked={formData.activa}
                onChange={(e) => setFormData({ ...formData, activa: e.target.checked })}
              />
              <Label check for="activa">
                Tarifa activa
              </Label>
            </FormGroup>
          </ModalBody>
          <ModalFooter>
            <Button color="secondary" onClick={toggleModal}>
              Cancelar
            </Button>
            <Button color="primary" type="submit">
              {editingTarifa ? 'Actualizar' : 'Crear'}
            </Button>
          </ModalFooter>
        </Form>
      </Modal>


    </Container>
    </div>

  );
};

export default Tarifas;